// The listino modals (TD-083, DD-045): one line, a line the listino before had and this one dropped, adding a line,
// a new listino, and the listino itself (its name and start date). A listino is the website's "from" prices: a line is
// a price, with what it includes and a description for the admin (finance-real round 3, the user 2026-10-30), and the
// split defaults live on the service line (TD-078). A line's drawer also renames its service line, and Add service line
// takes the whole line, its description and items included, in one step (the user 2026-10-31). Every listino stays editable, started or not, because nothing on a contract reads it. Removing a line, or
// deleting the listino, asks in the drawer's own footer.
import { useState } from "react";
import { SpinnerIcon, TrashIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { FooterConfirm, useFooterStep } from "@/components/ui/FooterConfirm";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { MoneyInput } from "@/components/ui/MoneyInput";
import { MoneyText } from "@/components/ui/MoneyText";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { AddRowButton } from "../components/AddRowButton";
import { MissingLine, Required } from "../components/Required";
import { WithoutIva } from "../components/WithoutIva";
import {
  useAddListinoLine, useCreateListino, useCreateServiceLine, useDeleteListino, useRemoveListinoLine, useUpdateListino, useUpdateListinoLine, useUpdateServiceLine,
} from "../hooks/usePricing";
import { useSubmitOnce } from "../hooks/useSubmitOnce";
import type { Listino, ListinoInclude, ListinoLine, ListinoLineKind, ServiceLine } from "../types";
import { LABEL_MAX, labelCheck } from "../utils/catalogs";
import { LISTINO_NAME_MAX, listinoCheck, newListinoDefaults, type ListinoCheck } from "../utils/listini";
import { deleteListinoMessage } from "../utils/listinoDiff";
import { closeUnlessPending } from "../utils/mutations";

const clientWord = ENTITY_CONFIG.client.label.toLowerCase();
const contractWord = ENTITY_CONFIG.contract.label.toLowerCase();
const LISTINO = ENTITY_CONFIG.listino;
const SERVICE_LINE = ENTITY_CONFIG.serviceLine;
const FROM_HINT = "The website shows it while this listino is the one in force.";
const per = (kind: ListinoLineKind): string => (kind === "monthly" ? "per month" : "one-off");

interface LineModalProps {
  listino: Listino;
  line: ListinoLine;
  serviceLineLabel: string;
  /** The catalog, for the service line's Name: a name another service line holds is refused here as the server does. */
  serviceLines: ServiceLine[];
  confirmRemove: boolean;
  onConfirmRemove: (on: boolean) => void;
  onClose: () => void;
}

type Unit = "amount" | "pct";

/** One item as the form holds it: its row key, the unit picked, and the value for it. */
interface IncludeDraft {
  key: string;
  label: string;
  unit: Unit;
  amountMinor: number | null;
  pct: number | null;
}

/** listino_service.MAX_INCLUDE_LABEL: the longest title the server keeps. */
const INCLUDE_LABEL_MAX = 80;
/** listino_service.MAX_DESCRIPTION: the longest description the server keeps. */
export const DESCRIPTION_MAX = 1000;
const DESCRIPTION_HINT = "For the team: what the line is and how it is sold. The website never shows it.";
const INCLUDES_HINT =
  `What the price covers, item by item: each an amount without IVA or a whole percent of the price. The website shows only the price, and who does the work stays the ${SERVICE_LINE.label.toLowerCase()}'s split.`;

let itemSeq = 0;
const nextItemKey = () => `i${++itemSeq}`;

const draftOf = (i: ListinoInclude): IncludeDraft => ({ key: nextItemKey(), label: i.label, unit: i.pct !== null ? "pct" : "amount", amountMinor: i.amountMinor, pct: i.pct });
const blankItem = (): IncludeDraft => ({ key: nextItemKey(), label: "", unit: "amount", amountMinor: null, pct: null });
const includeOf = (d: IncludeDraft): ListinoInclude =>
  d.unit === "pct" ? { label: d.label.trim(), amountMinor: null, pct: d.pct } : { label: d.label.trim(), amountMinor: d.amountMinor, pct: null };
const sameIncludes = (a: ListinoInclude[], b: ListinoInclude[]): boolean =>
  a.length === b.length && a.every((x, i) => x.label === b[i].label && x.amountMinor === b[i].amountMinor && x.pct === b[i].pct);
/** The server's per-item check (listino_service.parse_includes): a title, and an amount above zero or a whole percent from 1 to 100. */
const draftReady = (d: IncludeDraft): boolean =>
  d.label.trim() !== "" && (d.unit === "pct" ? d.pct !== null && Number.isInteger(d.pct) && d.pct >= 1 && d.pct <= 100 : d.amountMinor !== null && d.amountMinor > 0);

/** The form's preview, as Pay's running total: what the items typed come to against the price, a percent taken of it. */
function itemisedText(drafts: IncludeDraft[], fromMinor: number, currency: string): string {
  const itemised = drafts.reduce((sum, d) => sum + (d.unit === "pct" ? Math.round(((d.pct ?? 0) * fromMinor) / 100) : d.amountMinor ?? 0), 0);
  const rest = fromMinor - itemised;
  const tail = rest > 0 ? ` · ${formatMoney(rest, currency)} not itemised` : rest < 0 ? ` · ${formatMoney(-rest, currency)} over the price` : "";
  return `Itemised ${formatMoney(itemised, currency)} of ${formatMoney(fromMinor, currency)}${tail}`;
}

/** A line's description (the user 2026-10-30): an internal note, as the line drawer and Add service line both edit it. */
function DescriptionField({ value, disabled, onChange }: { value: string; disabled: boolean; onChange: (value: string) => void }) {
  return (
    <FormField label="Description" hint={DESCRIPTION_HINT}>
      <textarea
        className={inputClassName}
        aria-label="Description"
        rows={3}
        maxLength={DESCRIPTION_MAX}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    </FormField>
  );
}

const RENAME_HINT = "Renames the service line on every listino and job that names it.";

/**
 * What the price includes (finance-real round 3): titled items, each an amount or a percent of the price, in order. For
 * the admin: the website shows the price alone, and who does the work, and for how much, stays the service line's split
 * (TD-078).
 */
function IncludesEditor({ drafts, fromMinor, currency, disabled, onChange }: { drafts: IncludeDraft[]; fromMinor: number | null; currency: string; disabled: boolean; onChange: (drafts: IncludeDraft[]) => void }) {
  const set = (i: number, patch: Partial<IncludeDraft>) => onChange(drafts.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  return (
    <section className="space-y-2">
      <h3 className="text-[11px] uppercase tracking-wide text-gray-500">What the price includes</h3>
      <p className="text-xs text-gray-500">{INCLUDES_HINT}</p>
      {drafts.length > 0 && (
        <ul className="space-y-2">
          {drafts.map((d, i) => (
            <li key={d.key} className="grid grid-cols-[minmax(0,1fr)_5rem_8rem_auto] items-center gap-2">
              <input
                className={inputClassName}
                aria-label={`Item ${i + 1} title`}
                aria-required="true"
                placeholder="e.g. Shooting"
                maxLength={INCLUDE_LABEL_MAX}
                value={d.label}
                disabled={disabled}
                onChange={(e) => set(i, { label: e.target.value })}
              />
              <select
                aria-label={`Item ${i + 1} unit`}
                className={inputClassName}
                value={d.unit}
                disabled={disabled}
                onChange={(e) => set(i, { unit: e.target.value === "pct" ? "pct" : "amount" })}
              >
                <option value="amount">{currency === "EUR" ? "€" : currency}</option>
                <option value="pct">%</option>
              </select>
              {d.unit === "pct" ? (
                <input
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  className={inputClassName}
                  aria-label={`Item ${i + 1} percent`}
                  aria-required="true"
                  value={d.pct ?? ""}
                  disabled={disabled}
                  onChange={(e) => set(i, { pct: e.target.value === "" ? null : Number(e.target.value) })}
                />
              ) : (
                <MoneyInput valueMinor={d.amountMinor} currency={currency} onChange={(v) => set(i, { amountMinor: v })} ariaLabel={`Item ${i + 1} amount`} disabled={disabled} required />
              )}
              <Button variant="ghost" size="sm" aria-label={`Remove item ${i + 1}`} title={`Remove item ${i + 1}`} disabled={disabled} onClick={() => onChange(drafts.filter((_, j) => j !== i))}>
                <TrashIcon className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <AddRowButton disabled={disabled} onClick={() => onChange([...drafts, blankItem()])}>Add an item</AddRowButton>
        {drafts.length > 0 && fromMinor ? <span className="text-xs text-gray-500">{itemisedText(drafts, fromMinor, currency)}</span> : null}
      </div>
    </section>
  );
}

export function LineModal({ listino, line, serviceLineLabel, serviceLines, confirmRemove: confirming, onConfirmRemove, onClose }: LineModalProps) {
  // The service line's name: shown once the catalog holds it, checked against the other service lines' names.
  const serviceLine = serviceLines.find((s) => s.id === line.serviceLineId);
  // Untyped, it follows the catalog, so a catalog arriving after the drawer opened still fills it.
  const [typed, setName] = useState<string | null>(null);
  const name = typed ?? serviceLine?.label ?? "";
  const nameCheck = serviceLine ? labelCheck(SERVICE_LINE.label.toLowerCase(), serviceLines, serviceLine, name) : null;
  const renaming = !!serviceLine && !!nameCheck && nameCheck.error === null && nameCheck.label !== serviceLine.label;
  const [from, setFrom] = useState<number | null>(line.fromPriceMinor);
  const [drafts, setDrafts] = useState<IncludeDraft[]>(() => line.includes.map(draftOf));
  const [description, setDescription] = useState(line.description ?? "");
  const itemsReady = drafts.every(draftReady);
  const update = useUpdateListinoLine();
  const rename = useUpdateServiceLine();
  const remove = useRemoveListinoLine();
  // The step lives in the URL (the row menu opens at it).
  const footerStep = useFooterStep<"remove">(null, { step: confirming ? "remove" : null, go: (step) => onConfirmRemove(step !== null) });
  const pending = update.isPending || rename.isPending || remove.isPending;
  const send = useSubmitOnce();
  // mutateAsync, not mutate callbacks: a removal's refetch unmounts this modal first, and mutate callbacks die with
  // it. A refusal (toasted by the mutation) leaves the remove confirm, so it never offers itself again.
  const settle = (request: () => Promise<unknown>) =>
    send(request, () => onClose(), () => {
      if (footerStep.step) footerStep.leave();
    });
  // The items and the description go only when edited here: a drawer opened before another tab saved this line's would
  // otherwise write its stale copy back. Left out, the server keeps them (R3). A new name goes first, as its own request
  // on the service line: refused, the line is not saved either and the drawer stays open on what was typed.
  const save = () => {
    const includes = drafts.map(includeOf);
    const edited = !sameIncludes(includes, line.includes);
    const described = description.trim() !== (line.description ?? "");
    return settle(async () => {
      if (renaming && nameCheck) await rename.mutateAsync({ id: line.serviceLineId, patch: { label: nameCheck.label } });
      await update.mutateAsync({
        id: listino.id,
        lineId: line.id,
        line: { fromPriceMinor: from ?? line.fromPriceMinor, ...(edited ? { includes } : {}), ...(described ? { description: description.trim() || null } : {}) },
      });
    });
  };
  const nameHeld = !!nameCheck && nameCheck.error !== null;
  const doRemove = () => settle(() => remove.mutateAsync({ id: listino.id, lineId: line.id }));
  const footer =
    footerStep.step === "remove" ? (
      <FooterConfirm confirmLabel="Remove line" onConfirm={doRemove} onKeep={footerStep.leave} pending={pending} loading={remove.isPending}>
        {`Remove ${serviceLineLabel} from ${listino.name}? No ${clientWord}’s price changes.`}
      </FooterConfirm>
    ) : (
      <>
        <Button
          variant="ghost"
          className="mr-auto text-red-600 hover:text-red-700"
          onClick={() => footerStep.ask("remove")}
          disabled={pending}
          autoFocus={footerStep.returnsTo("remove")}
        >
          Remove line
        </Button>
        <Button onClick={save} disabled={!from || !itemsReady || nameHeld || pending} isLoading={update.isPending || rename.isPending}>Save</Button>
      </>
    );
  return (
    <FinanceDrawer onClose={closeUnlessPending(pending, onClose)} title={`${serviceLineLabel} · ${listino.name}`} footer={footer}>
      <div className="space-y-3">
        {serviceLine && (
          <FormField label={<Required>{`${SERVICE_LINE.label} name`}</Required>} hint={RENAME_HINT} error={nameCheck?.error ?? undefined}>
            <input
              className={inputClassName}
              aria-label={`${SERVICE_LINE.label} name`}
              aria-required="true"
              maxLength={LABEL_MAX + 20}
              value={name}
              disabled={pending}
              onChange={(e) => setName(e.target.value)}
            />
          </FormField>
        )}
        <FormField label={<Required>{`From price, ${per(line.kind)}`}</Required>} hint={FROM_HINT} className="w-64">
          <MoneyInput valueMinor={from} currency={listino.currency} onChange={setFrom} ariaLabel="From price" required />
          <WithoutIva />
        </FormField>
        <DescriptionField value={description} disabled={pending} onChange={setDescription} />
        <IncludesEditor drafts={drafts} fromMinor={from} currency={listino.currency} disabled={pending} onChange={setDrafts} />
        <MissingLine
          button="Save"
          missing={[...(nameHeld ? ["a free service line name"] : []), ...(from ? [] : ["the price"]), ...(itemsReady ? [] : ["a title for each item, and an amount or a whole percent from 1 to 100"])]}
        />
      </div>
    </FinanceDrawer>
  );
}

const NEW_LINE = "__new";

export function AddLineModal({ listino, serviceLines, loading, onClose }: { listino: Listino; serviceLines: ServiceLine[]; loading: boolean; onClose: () => void }) {
  const free = serviceLines.filter((s) => !listino.lines.some((l) => l.serviceLineId === s.id));
  // Nothing picked yet means the first free service line, whenever the catalog arrives.
  const [picked, setPicked] = useState<string | null>(null);
  const serviceLineId = picked ?? free[0]?.id ?? NEW_LINE;
  const [newName, setNewName] = useState("");
  const [kind, setKind] = useState<ListinoLineKind>("monthly");
  const [from, setFrom] = useState<number | null>(null);
  const [description, setDescription] = useState("");
  const [drafts, setDrafts] = useState<IncludeDraft[]>([]);
  const itemsReady = drafts.every(draftReady);
  const addLine = useAddListinoLine();
  const createServiceLine = useCreateServiceLine();
  const valid = !!from && from > 0 && itemsReady && (serviceLineId !== NEW_LINE || !!newName.trim());
  // What Add line waits for, in form order.
  const missing = [
    ...(serviceLineId === NEW_LINE && !newName.trim() ? ["the service line name"] : []),
    ...(from ? [] : ["the price"]),
    ...(itemsReady ? [] : ["a title for each item, and an amount or a whole percent from 1 to 100"]),
  ];
  // A new service line takes two requests, so the modal tracks the pair: it waits from the first to the last.
  const [sending, setSending] = useState(false);
  const sendOnce = useSubmitOnce();
  const send = async () => {
    setSending(true);
    const id = serviceLineId === NEW_LINE ? (await createServiceLine.mutateAsync(newName.trim())).id : serviceLineId;
    // The whole line in one add: its description and items go with the price (the user 2026-10-31).
    const note = description.trim();
    await addLine.mutateAsync({
      id: listino.id,
      line: { serviceLineId: id, kind, fromPriceMinor: from ?? 0, ...(drafts.length > 0 ? { includes: drafts.map(includeOf) } : {}), ...(note ? { description: note } : {}) },
    });
  };
  // Each mutation toasts its own refusal.
  const save = () =>
    sendOnce(
      send,
      () => {
        setSending(false);
        onClose();
      },
      () => setSending(false),
    );
  return (
    <FinanceDrawer
      onClose={closeUnlessPending(sending, onClose)}
      title={`${SERVICE_LINE.createLabel} · ${listino.name}`}
      footer={
        <>
          <Button onClick={save} disabled={loading || !valid || sending} isLoading={sending}>Add line</Button>
        </>
      }
    >
      {loading ? (
        <div className="flex justify-center py-12">
          <SpinnerIcon className="w-6 h-6 text-gray-400" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FormField label={SERVICE_LINE.label}>
              <select className={inputClassName} value={serviceLineId} onChange={(e) => setPicked(e.target.value)}>
                {free.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                <option value={NEW_LINE}>New service line…</option>
              </select>
            </FormField>
            <FormField label="Usually sold">
              <select className={inputClassName} value={kind} onChange={(e) => setKind(e.target.value === "one_time" ? "one_time" : "monthly")}>
                <option value="monthly">Per month</option>
                <option value="one_time">One-off</option>
              </select>
            </FormField>
          </div>
          {serviceLineId === NEW_LINE && (
            <FormField label={<Required>Name</Required>} hint={`e.g. Design. It becomes a service line every ${contractWord} line can pick.`}>
              <input className={inputClassName} aria-label="Name" aria-required="true" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
            </FormField>
          )}
          <FormField label={<Required>From price</Required>} hint={FROM_HINT} className="w-56">
            <MoneyInput valueMinor={from} currency={listino.currency} onChange={setFrom} ariaLabel="From price" required />
            <WithoutIva />
          </FormField>
          <DescriptionField value={description} disabled={sending} onChange={setDescription} />
          <IncludesEditor drafts={drafts} fromMinor={from} currency={listino.currency} disabled={sending} onChange={setDrafts} />
          <MissingLine button="Add line" missing={missing} />
        </div>
      )}
    </FinanceDrawer>
  );
}

interface EditListinoProps {
  listino: Listino;
  listini: Listino[];
  /** The server's today, for the delete question's fallback. */
  today: string;
  confirmDelete: boolean;
  onConfirmDelete: (on: boolean) => void;
  /** The listino is gone: close, and leave it unselected. */
  onDeleted: () => void;
  onClose: () => void;
}

// A listino's name and start date, any day, past included (TD-083). Nothing on a contract reads it, so a change moves only
// what the website shows. Deleting it asks in the footer (DD-045), the step held in the URL so the card's menu opens at it.
export function EditListinoModal({ listino, listini, today, confirmDelete: confirming, onConfirmDelete, onDeleted, onClose }: EditListinoProps) {
  const [name, setName] = useState(listino.name);
  const [start, setStart] = useState(listino.startDate);
  const update = useUpdateListino();
  const remove = useDeleteListino();
  const footerStep = useFooterStep<"delete">(null, { step: confirming ? "delete" : null, go: (step) => onConfirmDelete(step !== null) });
  const pending = update.isPending || remove.isPending;
  const send = useSubmitOnce();
  const check = listinoCheck({ name, startDate: start }, listini, listino.id);
  const save = () => send(() => update.mutateAsync({ id: listino.id, changes: { name: name.trim(), startDate: start } }), () => onClose());
  // mutateAsync, as the line's removal: the refetch unmounts this modal first. A refusal (toasted by the mutation) leaves the step.
  const doDelete = () =>
    send(() => remove.mutateAsync(listino.id), () => onDeleted(), () => {
      if (footerStep.step) footerStep.leave();
    });
  const footer =
    footerStep.step === "delete" ? (
      <FooterConfirm confirmLabel="Delete listino" onConfirm={doDelete} onKeep={footerStep.leave} pending={pending} loading={remove.isPending} stacked>
        {`Delete ${listino.name}? ${deleteListinoMessage(listino, listini, today)}`}
      </FooterConfirm>
    ) : (
      <>
        <Button
          variant="ghost"
          className="mr-auto text-red-600 hover:text-red-700"
          onClick={() => footerStep.ask("delete")}
          disabled={pending}
          autoFocus={footerStep.returnsTo("delete")}
        >
          Delete listino
        </Button>
        <Button onClick={save} disabled={check.blocked || pending} isLoading={update.isPending}>Save</Button>
      </>
    );
  return (
    <FinanceDrawer
      onClose={closeUnlessPending(pending, onClose)}
      title={`Edit listino · ${listino.name}`}
      footer={footer}
    >
      <div className="space-y-3">
        <ListinoFields name={name} start={start} check={check} onName={setName} onStart={setStart} />
      </div>
    </FinanceDrawer>
  );
}

/** A listino's name and start date, as both listino modals ask them. */
function ListinoFields({ name, start, check, onName, onStart }: { name: string; start: string; check: ListinoCheck; onName: (v: string) => void; onStart: (v: string) => void }) {
  return (
    <>
      <FormField label="Name" error={check.nameError}>
        <input className={inputClassName} value={name} maxLength={LISTINO_NAME_MAX} onChange={(e) => onName(e.target.value)} aria-label={`${ENTITY_CONFIG.listino.label} name`} />
      </FormField>
      <FormField label="Starts on" hint="The website switches to it that day. A past day records what it showed then." error={check.startError}>
        <input type="date" className={inputClassName} value={start} onChange={(e) => onStart(e.target.value)} aria-label="Starts on" />
      </FormField>
    </>
  );
}

interface NewListinoProps {
  /** The listino to copy: a version card's "New listino from this". None copies the active one, as the server does. */
  source: Listino | undefined;
  /** The listino the server copies without a source, for the hint. */
  active: Listino | undefined;
  listini: Listino[];
  today: string;
  /** The new listino, once it exists: the tab selects it. */
  onCreated: (created: Listino) => void;
  onClose: () => void;
}

// ?modal=listino-new[&copy=<id>] (DD-006, DD-045): a new listino opens a drawer first, never straight from a menu or
// the header. It opens on the server's own name and start (the next "Listino vN", the 1st after every other start),
// both changeable. Create copies the source's lines (TD-083).
export function NewListinoModal({ source, active, listini, today, onCreated, onClose }: NewListinoProps) {
  const [defaults] = useState(() => newListinoDefaults(listini, today));
  const [name, setName] = useState(defaults.name);
  const [start, setStart] = useState(defaults.startDate);
  const create = useCreateListino();
  const send = useSubmitOnce();
  const check = listinoCheck({ name, startDate: start }, listini, null);
  const copied = source ?? active;
  // One request: a refusal (toasted by the mutation) creates nothing and keeps the modal open to correct it.
  const save = () => send(() => create.mutateAsync({ copyOf: source?.id, name: name.trim(), startDate: start }), onCreated);
  return (
    <FinanceDrawer
      onClose={closeUnlessPending(create.isPending, onClose)}
      title={source ? `${LISTINO.createLabel} from ${source.name}` : LISTINO.createLabel}
      footer={
        <>
          <Button onClick={save} disabled={check.blocked || create.isPending} isLoading={create.isPending}>Create</Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-gray-600">{copied ? `It starts as a copy of ${copied.name}: its lines, all editable.` : "It starts empty: add its service lines once it exists."}</p>
        <ListinoFields name={name} start={start} check={check} onName={setName} onStart={setStart} />
      </div>
    </FinanceDrawer>
  );
}

interface RemovedLineProps {
  listino: Listino;
  /** The line as the listino before had it. */
  line: ListinoLine;
  previousName: string;
  serviceLineLabel: string;
  onClose: () => void;
}

// A line the listino before had and this one dropped (DD-045: its row's click opens it). It reads the old price, and
// Add back puts the line back as the listino before had it, its price, what it included and its description, a step in
// this drawer's footer.
export function RemovedLineModal({ listino, line, previousName, serviceLineLabel, onClose }: RemovedLineProps) {
  const add = useAddListinoLine();
  const send = useSubmitOnce();
  const addBack = () =>
    send(
      () => add.mutateAsync({ id: listino.id, line: { serviceLineId: line.serviceLineId, kind: line.kind, fromPriceMinor: line.fromPriceMinor, includes: line.includes, description: line.description } }),
      () => onClose(),
    );
  return (
    <FinanceDrawer
      onClose={closeUnlessPending(add.isPending, onClose)}
      title={`${serviceLineLabel} · removed from ${listino.name}`}
      footer={
        <>
          <Button onClick={addBack} disabled={add.isPending} isLoading={add.isPending}>Add back</Button>
        </>
      }
    >
      <p className="text-sm text-gray-600">
        {previousName} had it from <MoneyText amountMinor={line.fromPriceMinor} currency={listino.currency} strong /> {per(line.kind)}.{" "}
        {`${listino.name} drops it. Add back puts it back at this price${line.includes.length > 0 ? ", with what it included" : ""}.`}
      </p>
    </FinanceDrawer>
  );
}
